import React, { useRef, useEffect, useState } from 'react';
import './SignatureCanvas.css';

interface SignatureCanvasProps {
  onSave: (signatureData: string) => void;
  onCancel?: () => void;
  width?: number;
  height?: number;
}

type DrawEvent = React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>;

type OrientationWithLock = ScreenOrientation & {
  lock?: (orientation: string) => Promise<void>;
};

const pointFromEvent = (event: DrawEvent, canvas: HTMLCanvasElement, rotated: boolean) => {
  const rect = canvas.getBoundingClientRect();
  const source = 'touches' in event ? event.touches[0] : event;
  if (!source || rect.width === 0 || rect.height === 0) return null;

  if (!rotated) {
    return {
      x: ((source.clientX - rect.left) / rect.width) * canvas.width,
      y: ((source.clientY - rect.top) / rect.height) * canvas.height
    };
  }

  const localX = source.clientY - rect.top;
  const localY = rect.right - source.clientX;
  return {
    x: (localX / rect.height) * canvas.width,
    y: (localY / rect.width) * canvas.height
  };
};

const SignatureCanvas: React.FC<SignatureCanvasProps> = ({
  onSave,
  onCancel,
  width = 400,
  height = 200
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const erasingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const inkRef = useRef<string | null>(null);
  const hasSignatureRef = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [turned, setTurned] = useState(false);
  const [portrait, setPortrait] = useState(false);
  const [board, setBoard] = useState({ width, height });
  const rotated = turned && portrait;

  const rememberInk = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasSignatureRef.current) return;
    inkRef.current = canvas.toDataURL('image/png');
  };

  useEffect(() => {
    if (!turned) {
      setPortrait(false);
      setBoard((current) => (
        current.width === width && current.height === height ? current : { width, height }
      ));
      return;
    }

    const measure = () => {
      const landscape = window.innerWidth >= window.innerHeight;
      setPortrait(!landscape);
      const longSide = landscape ? window.innerWidth : window.innerHeight;
      const shortSide = landscape ? window.innerHeight : window.innerWidth;
      const next = {
        width: Math.max(640, Math.floor(longSide - 48)),
        height: Math.max(220, Math.floor(shortSide - 140))
      };
      setBoard((current) => (
        current.width === next.width && current.height === next.height ? current : next
      ));
    };

    measure();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [turned, width, height]);

  useEffect(() => () => {
    try {
      window.screen.orientation?.unlock?.();
    } catch {
      // A orientação volta quando o quadro fecha.
    }
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const ink = inkRef.current;
    if (ink) {
      const image = new Image();
      image.onload = () => {
        const current = canvasRef.current?.getContext('2d');
        if (!current || !canvasRef.current) return;
        current.drawImage(image, 0, 0, canvasRef.current.width, canvasRef.current.height);
      };
      image.src = ink;
    }

    const blockScroll = (event: TouchEvent) => {
      if (event.cancelable) event.preventDefault();
    };
    canvas.addEventListener('touchmove', blockScroll, { passive: false });
    return () => canvas.removeEventListener('touchmove', blockScroll);
  }, [board.width, board.height]);

  const strokeTo = (point: { x: number; y: number }) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const from = lastPointRef.current;
    if (!ctx || !from) return;

    const rubbing = erasingRef.current;
    const scale = turned ? canvas.width / width : 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = rubbing ? '#ffffff' : '#111827';
    ctx.lineWidth = (rubbing ? 18 : 2.25) * scale;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
  };

  const startDrawing = (event: DrawEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = pointFromEvent(event, canvas, rotated);
    if (!point) return;

    drawingRef.current = true;
    lastPointRef.current = point;
    hasSignatureRef.current = true;
    setHasSignature(true);
    strokeTo({ x: point.x + 0.01, y: point.y + 0.01 });
  };

  const draw = (event: DrawEvent) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = pointFromEvent(event, canvas, rotated);
    if (!point) return;
    strokeTo(point);
  };

  const stopDrawing = () => {
    drawingRef.current = false;
    lastPointRef.current = null;
    rememberInk();
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    drawingRef.current = false;
    lastPointRef.current = null;
    inkRef.current = null;
    hasSignatureRef.current = false;
    setHasSignature(false);
  };

  const usePen = () => {
    erasingRef.current = false;
    setErasing(false);
  };

  const useEraser = () => {
    erasingRef.current = true;
    setErasing(true);
  };

  const turnPhone = async () => {
    rememberInk();
    setTurned(true);
    const node = containerRef.current as (HTMLDivElement & { webkitRequestFullscreen?: () => Promise<void> | void }) | null;
    const request = node?.requestFullscreen?.bind(node) || node?.webkitRequestFullscreen?.bind(node);
    try {
      if (request) await request();
    } catch {
      // O quadro ainda gira quando o navegador recusa a tela cheia.
    }
    const orientation = window.screen.orientation as OrientationWithLock;
    try {
      await orientation?.lock?.('landscape');
    } catch {
      // O celular pode ser girado na mão quando o bloqueio de orientação é recusado.
    }
  };

  const upright = async () => {
    rememberInk();
    setTurned(false);
    try {
      window.screen.orientation?.unlock?.();
    } catch {
      // A orientação volta com o quadro.
    }
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch {
        // A tela cheia já pode ter sido encerrada.
      }
    }
  };

  const save = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (!hasSignature) {
      alert('Por favor, assine antes de salvar');
      return;
    }

    const signatureData = canvas.toDataURL('image/png');
    onSave(signatureData);
  };

  const containerClass = [
    'signature-canvas-container',
    turned ? 'is-turned' : '',
    rotated ? 'is-portrait' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClass} ref={containerRef}>
      <div className="signature-turn-board">
        {rotated && (
          <p className="signature-turn-hint">Gire o celular para assinar na horizontal.</p>
        )}
        <div className="signature-canvas-wrapper">
          <canvas
            ref={canvasRef}
            width={board.width}
            height={board.height}
            className={`signature-canvas${erasing ? ' is-erasing' : ''}`}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </div>
        <div className="signature-canvas-actions">
          <div className="signature-canvas-tools">
            <button
              type="button"
              onClick={usePen}
              className={`signature-btn signature-btn-tool${erasing ? '' : ' is-active'}`}
            >
              Caneta
            </button>
            <button
              type="button"
              onClick={useEraser}
              className={`signature-btn signature-btn-tool${erasing ? ' is-active' : ''}`}
            >
              Borracha
            </button>
            <button
              type="button"
              onClick={turned ? upright : turnPhone}
              className={`signature-btn signature-btn-tool${turned ? ' is-active' : ''}`}
            >
              {turned ? 'Desfazer giro' : 'Girar'}
            </button>
          </div>
          <button
            type="button"
            onClick={clear}
            className="signature-btn signature-btn-clear"
          >
            Limpar
          </button>
          <button
            type="button"
            onClick={save}
            className="signature-btn signature-btn-save"
            disabled={!hasSignature}
          >
            Confirmar Assinatura
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="signature-btn signature-btn-cancel"
            >
              Cancelar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignatureCanvas;
